import { useEffect, useRef, useState } from "react";
import { PostOrder } from "./api";

export type Capture = {
  lat: number;
  lng: number;
  accuracy: number;
  photo: Blob;
  acknowledgePostOrders?: boolean;
  handoverNote?: string;
  forceCheckoutThenCheckin?: boolean;
};

type Props = {
  guardName: string;
  // checkin: show post-orders ack modal before camera; also show previous
  // handover as a read-only banner. checkout: show handover textarea.
  kind: "checkin" | "checkout";
  postOrders?: PostOrder[];
  previousHandover?: string;
  // When true, the capture is a forgot-to-checkout recovery — the backend
  // will close the stale shift with the same photo/geo then open a new one.
  forceCheckoutThenCheckin?: boolean;
  onCapture: (c: Capture) => Promise<void>;
};

type Stage = "ack" | "intro" | "locating" | "camera" | "submitting" | "denied-geo" | "denied-camera";

type Facing = "user" | "environment";

export function CaptureFlow({ guardName, kind, postOrders, previousHandover, forceCheckoutThenCheckin, onCapture }: Props) {
  const hasPostOrders = kind === "checkin" && (postOrders?.length ?? 0) > 0;
  // If check-in has post orders, start on the ack modal; otherwise go
  // straight to intro.
  const [stage, setStage] = useState<Stage>(hasPostOrders ? "ack" : "intro");
  const [ackChecked, setAckChecked] = useState(false);
  const [handoverNote, setHandoverNote] = useState("");
  const [geo, setGeo] = useState<GeolocationPosition | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [facing, setFacing] = useState<Facing>("user");
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const requestGeo = () => {
    setStage("locating");
    setError(null);
    if (!navigator.geolocation) {
      setError("This device does not support location.");
      setStage("denied-geo");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeo(pos);
        setStage("camera");
      },
      (err) => {
        setError(err.message || "Location access was denied.");
        setStage("denied-geo");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  useEffect(() => {
    if (stage !== "camera") return;
    let cancelled = false;
    (async () => {
      try {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facing } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (err: any) {
        setError(err?.message || "Could not open the camera.");
        setStage("denied-camera");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stage, facing]);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => () => stopCamera(), []);

  const snapAndSubmit = async () => {
    if (!geo || !videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 720;
    canvas.height = video.videoHeight || 960;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.85),
    );
    if (!blob) {
      setError("Failed to capture the photo.");
      return;
    }
    stopCamera();
    setStage("submitting");
    try {
      await onCapture({
        lat: geo.coords.latitude,
        lng: geo.coords.longitude,
        accuracy: geo.coords.accuracy,
        photo: blob,
        acknowledgePostOrders: kind === "checkin" ? ackChecked || !hasPostOrders : undefined,
        handoverNote: kind === "checkout" ? handoverNote.trim() || undefined : undefined,
        forceCheckoutThenCheckin: forceCheckoutThenCheckin || undefined,
      });
    } catch (err: any) {
      setError(err?.message || "Submit failed.");
      setStage("intro");
    }
  };

  const introLabel = forceCheckoutThenCheckin
    ? `Check out stale shift & check in ${guardName}`
    : kind === "checkin"
      ? `Start check-in for ${guardName}`
      : `Start check-out for ${guardName}`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {stage === "ack" && hasPostOrders && (
        <div style={{ background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: 8, padding: "1rem" }}>
          <h3 style={{ margin: "0 0 0.5rem" }}>Post orders — please read</h3>
          <ol style={{ paddingLeft: "1.2rem", margin: "0.5rem 0" }}>
            {postOrders!.map((p) => (
              <li key={p.id} style={{ marginBottom: "0.5rem" }}>
                <strong>{p.name}</strong>
                {p.description && <> — <span style={{ color: "#555" }}>{p.description}</span></>}
                {p.orders && (
                  <div style={{ marginTop: "0.3rem", color: "#333", whiteSpace: "pre-wrap" }}>{p.orders}</div>
                )}
              </li>
            ))}
          </ol>
          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.8rem", cursor: "pointer" }}>
            <input type="checkbox" checked={ackChecked} onChange={(e) => setAckChecked(e.target.checked)} />
            <span>I've read and understood the post orders.</span>
          </label>
          <button
            onClick={() => setStage("intro")}
            disabled={!ackChecked}
            style={{ ...buttonStyle, marginTop: "0.8rem", opacity: ackChecked ? 1 : 0.5 }}
          >
            Continue
          </button>
        </div>
      )}

      {stage === "intro" && (
        <>
          {kind === "checkin" && previousHandover && (
            <div style={{ background: "#eff6ff", border: "1px solid #3b82f6", borderRadius: 8, padding: "0.8rem" }}>
              <strong>Previous handover:</strong>
              <div style={{ marginTop: "0.3rem", whiteSpace: "pre-wrap" }}>{previousHandover}</div>
            </div>
          )}
          <p>Confirm where you are and who you are:</p>
          <ul style={{ color: "#555" }}>
            <li>allow location access</li>
            <li>take a photo of <strong>{guardName}</strong></li>
          </ul>
          <button onClick={requestGeo} style={buttonStyle}>{introLabel}</button>
        </>
      )}

      {stage === "locating" && <p>Locating… please allow access.</p>}

      {stage === "denied-geo" && (
        <>
          <p style={{ color: "#b00" }}>Location is required. {error}</p>
          <button onClick={requestGeo} style={buttonStyle}>Try again</button>
        </>
      )}

      {stage === "denied-camera" && (
        <>
          <p style={{ color: "#b00" }}>Camera access is required. {error}</p>
          <button onClick={() => setStage("camera")} style={buttonStyle}>Try again</button>
        </>
      )}

      {stage === "camera" && (
        <>
          <video ref={videoRef} autoPlay playsInline muted
                 style={{ width: "100%", maxWidth: 480, background: "#000", borderRadius: 8 }} />
          <canvas ref={canvasRef} style={{ display: "none" }} />
          {kind === "checkout" && (
            <label style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
              <span style={{ fontSize: "0.9rem", color: "#444" }}>
                Handover note for the next guard (optional):
              </span>
              <textarea
                value={handoverNote}
                onChange={(e) => setHandoverNote(e.target.value.slice(0, 2000))}
                placeholder="e.g. camera 3 is offline — report said tech will visit tomorrow"
                rows={3}
                style={{ fontFamily: "inherit", fontSize: "0.95rem", padding: "0.5rem", borderRadius: 6, border: "1px solid #cbd5e1" }}
              />
              <small style={{ color: "#777" }}>{handoverNote.length}/2000</small>
            </label>
          )}
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <button onClick={snapAndSubmit} style={{ ...buttonStyle, flex: 1, minWidth: 180 }}>
              Snap + submit
            </button>
            <button
              onClick={() => setFacing((f) => (f === "user" ? "environment" : "user"))}
              style={{ ...buttonStyle, background: "#475569", flex: "0 0 auto" }}
              aria-label="Flip camera"
              title={facing === "user" ? "Switch to rear camera" : "Switch to selfie camera"}
            >
              {facing === "user" ? "Use rear camera" : "Use selfie"}
            </button>
          </div>
          <small style={{ color: "#777" }}>
            GPS: ±{geo?.coords.accuracy.toFixed(0)}m · camera: {facing === "user" ? "selfie" : "rear"}
          </small>
        </>
      )}

      {stage === "submitting" && <p>Submitting to Guardian…</p>}

      {error && stage !== "denied-geo" && stage !== "denied-camera" && (
        <p style={{ color: "#b00" }}>{error}</p>
      )}
    </div>
  );
}

const buttonStyle: React.CSSProperties = {
  padding: "0.9rem 1.2rem",
  fontSize: "1.05rem",
  background: "#1e40af",
  color: "white",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
};
